import React, { useState, useEffect, useRef } from "react";
import styles from "./styles.tsx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function Chatbot() {
  const serverAddress =
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://aiservice.databourse.au";
  
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hello I am DRAX. Your AI Assistant. How can I help you today? 😀",
      references: [],
      graph: null,
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [sessionId, setSessionId] = useState(() => {
    // Try to get session ID from localStorage
    return localStorage.getItem("databourse_session_id") || null;
  });
  const [lastUserMessage, setLastUserMessage] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const chatRef = useRef(null);
  const [serverOnline, setServerOnline] = useState(false);
  const [expandedGraph, setExpandedGraph] = useState(null);

  useEffect(() => {
    const checkServer = async () => {
      try {
        const res = await fetch(serverAddress + "/health");
        setServerOnline(res.ok);
      } catch {
        setServerOnline(false);
      }
    };

    checkServer();
    const interval = setInterval(checkServer, 10000);
    return () => clearInterval(interval);
  }, []);

  async function sendMessageStreaming() {
    if (!input.trim()) return;

    const userMessage = { sender: "user", text: input };
    const messageText = input;
    
    if (!isRetrying) {
      setMessages((msgs) => [...msgs, userMessage]);
    }
    
    setLastUserMessage(messageText);
    setInput("");
    setLoading(true);
    setHasError(false);
    setIsRetrying(false);
    setStatusMessage("Connecting...");

    try {
      const headers = {
        "Content-Type": "application/json",
      };
      
      // Add session ID to request headers if available
      if (sessionId) {
        headers["X-Session-ID"] = sessionId;
      }

      const response = await fetch(serverAddress + "/chat/stream", {
        method: "POST",
        headers,
        body: JSON.stringify({ message: messageText }),
      });

      if (!response.ok) {
        throw new Error("Stream request failed");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              
              // Capture session ID from first message
              if (data.type === "session_id") {
                const newSessionId = data.session_id;
                setSessionId(newSessionId);
                localStorage.setItem("databourse_session_id", newSessionId);
                console.log("Received session ID:", newSessionId);
                continue;
              }
              
              if (data.type === "status") {
                setStatusMessage(data.message);
              } else if (data.type === "response") {
                const botMessage = {
                  sender: "bot",
                  text: "",
                  references: [],
                  graph: null,
                };

                const referenceMatch = data.message.match(/<References>([\s\S]*?)<\/References>/);
                if (referenceMatch) {
                  const refs = [...referenceMatch[1].matchAll(/<Reference>(.*?)<\/Reference>/g)];
                  botMessage.references = refs.map((r) => r[1].trim());
                  botMessage.text = data.message.replace(/<References>[\s\S]*?<\/References>/, "").trim();
                } else {
                  botMessage.text = data.message;
                }

                const graphMatch = data.message.match(/<Graph>(.*?)<\/Graph>/s);
                if (graphMatch) {
                  botMessage.graph = graphMatch[1].trim();
                  botMessage.text = botMessage.text.replace(/<Graph>.*?<\/Graph>/s, "").trim();
                }

                setMessages((msgs) => [...msgs, botMessage]);
                setStatusMessage("");
              } else if (data.type === "error") {
                setMessages((msgs) => [
                  ...msgs,
                  { sender: "bot", text: data.message, references: [], graph: null, isError: true },
                ]);
                setHasError(true);
                setStatusMessage("");
              }
            } catch (e) {
              console.error("Failed to parse SSE data:", e);
            }
          }
        }
      }
    } catch (error) {
      console.error("Streaming error:", error);
      setMessages((msgs) => [
        ...msgs,
        { sender: "bot", text: "Oops! Something went wrong.", references: [], graph: null, isError: true },
      ]);
      setHasError(true);
    } finally {
      setLoading(false);
      setStatusMessage("");
    }
  }

  function clearChat() {
    setMessages([
      {
        sender: "bot",
        text: "Hello I am DRAX. Your AI Assistant. How can I help you today? 😀",
        references: [],
        graph: null,
      },
    ]);
    // Clear session ID on new chat
    setSessionId(null);
    localStorage.removeItem("databourse_session_id");
    setLastUserMessage(null);
    setHasError(false);
    setIsRetrying(false);
  }

  async function retryLastMessage() {
    if (!lastUserMessage) return;
    setInput(lastUserMessage);
    // Remove the error message before retrying
    setMessages((msgs) => msgs.slice(0, -1));
    setHasError(false);
    setIsRetrying(true);
    // Use setTimeout to ensure state updates are processed
    setTimeout(() => {
      document.querySelector('[data-send-button]').click();
    }, 0);
  }

  function exportChat() {
    const chatText = messages
      .map((msg) => {
        let baseText = `${msg.sender.toUpperCase()}: ${msg.text}`;
        if (msg.references?.length) {
          baseText += `\nReferences: ${msg.references.join(", ")}`;
        }
        if (msg.graph) {
          baseText += `\n[Graph included]`;
        }
        return baseText;
      })
      .join("\n\n");

    const blob = new Blob([chatText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `chat_${new Date().toISOString().replace(/[:.]/g, "-")}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  function downloadGraph(graphData) {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${graphData}`;
    a.download = `graph_${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  useEffect(() => {
    chatRef.current?.scrollTo(0, chatRef.current.scrollHeight);
  }, [messages, loading, statusMessage]);

  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `
      @keyframes bounce {
        0%, 80%, 100% { transform: scale(0); }
        40% { transform: scale(1); }
      }
      .typing span {
        display: inline-block;
        animation: bounce 1.4s infinite;
        font-size: 24px;
        line-height: 24px;
      }
      .typing span:nth-child(1) { animation-delay: 0s; }
      .typing span:nth-child(2) { animation-delay: 0.2s; }
      .typing span:nth-child(3) { animation-delay: 0.4s; }
      
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      .status-message {
        animation: pulse 1.5s ease-in-out infinite;
      }
    `;
    document.head.appendChild(styleTag);
    return () => document.head.removeChild(styleTag);
  }, []);

  return (
    <div style={styles.container}>
      {expandedGraph && (
        <div
          style={styles.modalOverlay}
          onClick={() => setExpandedGraph(null)}
        >
          <div style={styles.modalContent}>
            <img
              src={`data:image/png;base64,${expandedGraph}`}
              alt="Expanded graph"
              style={styles.modalImage}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedGraph(null);
              }}
              style={styles.modalCloseButton}
            >
              ✕
            </button>
          </div>
        </div>
      )}
      
      <div style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: serverOnline ? "limegreen" : "red",
              border: "2px solid white",
              display: "inline-block",
            }}
            title={serverOnline ? "Server online" : "Server offline"}
          />
          <span style={styles.title}>CHAT WITH DRAX</span>
        </div>
        <div>
          <button onClick={clearChat} style={styles.clearButton}>
            Clear Chat
          </button>
          <button onClick={exportChat} style={styles.clearButton}>
            Export Chat
          </button>
          <span style={styles.close}>×</span>
        </div>
      </div>

      <div ref={chatRef} style={styles.chatWindow}>
        {messages.map((msg, idx) => (
          <div
            key={idx}
            style={{
              ...styles.message,
              justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
            }}
          >
            <div
              style={{
                ...styles.bubble,
                backgroundColor: msg.sender === "user" ? "#4a4a4a" : "#55555f",
                color: "#fff",
              }}
            >
              <div className="markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
              </div>

              {msg.graph && (
                <div style={styles.graphContainer}>
                  <div style={{ position: "relative", display: "inline-block", maxWidth: "60%" }}>
                    <img
                      src={`data:image/png;base64,${msg.graph}`}
                      alt="Generated graph"
                      style={styles.graphImage}
                    />
                    <div style={styles.graphButtonContainer}>
                      <button
                        onClick={() => setExpandedGraph(msg.graph)}
                        style={styles.graphButton}
                        title="Expand graph"
                      >
                        ⛶
                      </button>
                      <button
                        onClick={() => downloadGraph(msg.graph)}
                        style={styles.graphButton}
                        title="Download graph"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {msg.references?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>References:</div>
                  <ul style={{ listStyle: "none", padding: 0 }}>
                    {msg.references.map((ref, i) => (
                      <li
                        key={i}
                        style={{
                          display: "inline-block",
                          backgroundColor: "#eee",
                          color: "#333",
                          borderRadius: "12px",
                          padding: "2px 8px",
                          margin: "2px",
                          fontSize: "0.8em",
                        }}
                      >
                        {ref}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {msg.isError && (
                <div style={{ marginTop: 12, marginBottom: 8,display: "flex", gap: 8 }}>
                  <button
                    onClick={retryLastMessage}
                    disabled={loading}
                    style={{
                      ...styles.sendButton,
                      padding: "6px 12px",
                      cursor: loading ? "not-allowed" : "pointer",
                      opacity: loading ? 0.6 : 1,
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ ...styles.message, justifyContent: "flex-start" }}>
            <div
              style={{
                ...styles.bubble,
                padding: "12px 16px",
                backgroundColor: "#55555f",
                color: "#fff",
              }}
            >
              {statusMessage ? (
                <div className="status-message" style={{ fontSize: "0.95em" }}>
                  {statusMessage}
                </div>
              ) : (
                <div className="typing">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={styles.inputArea}>
        <input
          value={input}
          title={serverOnline ? "Send message" : "Server is offline"}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && serverOnline) {
              sendMessageStreaming();
            } else if (e.key === "Enter" && !serverOnline) {
              e.preventDefault();
            }
          }}
          placeholder="Type your message..."
          style={styles.input}
        />
        <button
          disabled={!serverOnline}
          onClick={sendMessageStreaming}
          data-send-button="true"
          style={{
            ...styles.sendButton,
            opacity: serverOnline ? 1 : 0.5,
            cursor: serverOnline ? "pointer" : "not-allowed",
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}